/**
 * Utilities for splitting and merging predefined vs custom (clinician-typed)
 * dropdown selections in MSE and Risk Assessment forms.
 *
 * Both the MSE (10_MSE.js) and RSK (11_RSK.js) forms have a pattern where each
 * category row has a Yes/No toggle button paired with a Creatable multi-select
 * dropdown.  The dropdown's option list comes from predefined yesOptions/noOptions,
 * but clinicians can also type free-text entries (custom entries) via the Creatable
 * component.
 *
 * When the clinician toggles Yes/No, we need to:
 *  1. Replace any existing predefined selections with the new default
 *  2. Preserve any custom (free-text) entries the clinician has typed
 *
 * This module provides the shared logic for that split-and-merge operation,
 * eliminating duplication between the MSE and RSK components.
 */

/**
 * Separates selected options into predefined (values that appear in yesOptions
 * or noOptions) and custom (clinician-typed free-text) buckets.
 *
 * @param {Array<{value: string, label: string}>} selectedOptions - current selections from the dropdown
 * @param {Array<string>} yesOptions - predefined option values for the "Yes" / "Typical" state
 * @param {Array<string>} noOptions  - predefined option values for the "No" / "Atypical" state
 * @returns {{ predefined: Array, custom: Array }} the two buckets
 */
export function splitPredefinedAndCustom(selectedOptions, yesOptions = [], noOptions = []) {
  const allPredefined = new Set([...yesOptions, ...noOptions]);
  const predefined = (selectedOptions || []).filter(opt => allPredefined.has(opt.value));
  const custom = (selectedOptions || []).filter(opt => !allPredefined.has(opt.value));
  return { predefined, custom };
}

/**
 * Builds a merged selection array after a Yes/No toggle: discards all existing
 * predefined selections, inserts the appropriate default for the new button
 * state, and appends all custom entries unchanged.
 *
 * The caller determines what the default should be:
 *   - MSE "Yes"  -> first yesOption
 *   - MSE "No"   -> null (no default)
 *   - RSK "Yes"  -> null (no default)
 *   - RSK "No"   -> first noOption (e.g. "None")
 *   - Un-select  -> null (no default)
 *
 * @param {Array<{value: string, label: string}>} existingSelections - current selectedOption array
 * @param {Array<string>} yesOptions  - predefined option values for the "Yes" state
 * @param {Array<string>} noOptions   - predefined option values for the "No" state
 * @param {string|null}   defaultValue - the single predefined value to auto-populate, or null for none
 * @returns {Array<{value: string, label: string}>} merged selection array
 */
export function buildMergedSelection(existingSelections, yesOptions, noOptions, defaultValue) {
  const { custom } = splitPredefinedAndCustom(existingSelections, yesOptions, noOptions);
  // Only suppress the default when it is a noOptions sentinel (e.g. "None") and
  // custom entries exist. This follows the noneOptions pattern (FormTemplate.js,
  // 3_TRT.js): real/custom content takes precedence over a placeholder sentinel.
  // Non-sentinel defaults (e.g. MSE's "Normal, rapport easily established") are
  // always injected — they are meaningful clinical observations, not placeholders.
  const isSentinel = noOptions.includes(defaultValue);
  const newDefault = (defaultValue != null && (!isSentinel || custom.length === 0))
    ? [{ value: defaultValue, label: defaultValue }]
    : [];
  return [...newDefault, ...custom];
}
